import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";

// CSS mirrors Employees.js / Payroll.js / DocumentExpiry.js tokens 1:1 so
// this reads as the same module. Reuses the same severity tokens
// DocumentExpiry.js introduced (--danger / --warn), just applied to
// data-quality issue severity instead of expiry urgency.
const CSS = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg: #f0f4fc; --surface: #ffffff; --surface2: #f4f8ff;
  --border: #c0d0eb; --border2: #d5e3f5;
  --accent: #0284c7; --text: #0d1a36; --text2: #2a4a78; --muted: #6080aa;
  --danger: #dc2626; --danger-bg: rgba(220,38,38,.07); --danger-border: rgba(220,38,38,.22);
  --warn: #b45309; --warn-bg: rgba(180,83,9,.07); --warn-border: rgba(180,83,9,.22);
  --ok: #047857; --ok-bg: rgba(4,120,87,.07); --ok-border: rgba(4,120,87,.22);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color-scheme: light;
}
body { background: var(--bg); color: var(--text); }
.adm-topbar { position: sticky; top: 0; z-index: 100; height: 52px; display: flex; align-items: center; background: rgba(240,244,252,.92); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border2); }
.adm-back { width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; background: none; border: none; border-right: 1px solid var(--border2); color: var(--text2); cursor: pointer; font-size: 16px; flex-shrink: 0; transition: color .15s, background .15s; }
.adm-back:hover { color: var(--text); background: var(--surface); }
.adm-brand { display: flex; align-items: center; gap: 10px; padding: 0 20px; flex: 1; }
.adm-brand-mark { width: 26px; height: 26px; border-radius: 6px; background: linear-gradient(135deg, #0284c7, #2563eb); display: flex; align-items: center; justify-content: center; font-size: 13px; }
.adm-brand-label { font-size: 14px; font-weight: 600; color: var(--text); }
.adm-avatar { margin-right: 16px; width: 30px; height: 30px; border-radius: 50%; background: linear-gradient(135deg, #2563eb, #7c3aed); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: #fff; flex-shrink: 0; }
.adm-tabs { display: flex; align-items: center; gap: 2px; padding: 0 20px; border-bottom: 1px solid var(--border2); background: var(--surface); overflow-x: auto; }
.adm-tab { padding: 14px 16px; font-size: 13px; font-weight: 500; color: var(--muted); background: none; border: none; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color .15s; font-family: inherit; white-space: nowrap; }
.adm-tab:hover { color: var(--text2); }
.adm-tab.active { color: var(--accent); font-weight: 700; border-bottom-color: var(--accent); }
.adm-main { max-width: 1100px; margin: 0 auto; padding: 28px 24px 80px; }
.adm-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 24px; margin-bottom: 14px; box-shadow: 0 1px 2px rgba(13,26,54,.04), 0 4px 12px rgba(13,26,54,.05); }
.adm-card-title { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
.adm-card-desc { font-size: 12px; color: var(--muted); margin-bottom: 16px; line-height: 1.5; }
.adm-error { padding: 10px 14px; border-radius: 7px; background: rgba(248,113,113,.08); border: 1px solid rgba(248,113,113,.2); color: #dc2626; font-size: 13px; margin-bottom: 14px; }
.adm-spinner-wrap { display: flex; justify-content: center; padding: 40px 0; }
.adm-spinner { width: 26px; height: 26px; border-radius: 50%; border: 3px solid var(--border2); border-top-color: var(--accent); animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.adm-empty { font-size: 13px; color: var(--muted); padding: 30px 20px; text-align: center; border: 1px dashed var(--border2); border-radius: 8px; background: var(--surface2); }
.adm-input { background: var(--surface2); border: 1px solid var(--border2); border-radius: 7px; padding: 9px 12px; font-size: 13px; color: var(--text); outline: none; font-family: inherit; transition: border-color .15s; width: 100%; }
.adm-input:focus { border-color: var(--accent); }
.adm-action-btn { background: none; border: 1px solid var(--border); border-radius: 6px; padding: 5px 11px; font-size: 12px; color: var(--text2); cursor: pointer; font-family: inherit; transition: border-color .15s, color .15s; white-space: nowrap; }
.adm-action-btn:hover { border-color: var(--accent); color: var(--accent); }

.dq-page-head { display: flex; justify-content: space-between; align-items: flex-end; flex-wrap: wrap; gap: 10px; margin-bottom: 20px; }
.dq-page-head h1 { font-family: inherit; font-size: 20px; font-weight: 700; color: var(--text); }
.dq-page-head p { font-size: 12.5px; color: var(--muted); margin-top: 3px; }
.dq-asof { font-size: 11px; color: var(--muted); font-variant-numeric: tabular-nums; }

.dq-tiles { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 22px; }
@media(max-width: 640px) { .dq-tiles { grid-template-columns: 1fr; } }
.dq-tile { text-align: left; background: var(--surface); border: 1px solid var(--border); border-left: 4px solid var(--tile-c); border-radius: 9px; padding: 16px; }
.dq-tile .n { font-size: 26px; font-weight: 800; color: var(--tile-c); line-height: 1; font-variant-numeric: tabular-nums; }
.dq-tile .l { font-size: 11.5px; font-weight: 600; color: var(--text2); margin-top: 7px; text-transform: uppercase; letter-spacing: .03em; }

.dq-check { border: 1px solid var(--border); border-radius: 10px; margin-bottom: 10px; overflow: hidden; background: var(--surface); }
.dq-check-head { display: flex; align-items: center; gap: 12px; width: 100%; padding: 14px 16px; background: none; border: none; cursor: pointer; font-family: inherit; text-align: left; transition: background .12s; }
.dq-check-head:hover { background: var(--surface2); }
.dq-check-head:disabled { cursor: default; }
.dq-sev { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.dq-check-label { font-size: 13.5px; font-weight: 600; color: var(--text); flex: 1; }
.dq-count { font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums; padding: 2px 10px; border-radius: 20px; border: 1px solid; }
.dq-chev { color: var(--muted); font-size: 11px; width: 12px; text-align: center; }
.dq-check-body { padding: 4px 16px 14px; }
.dq-check-body .desc { font-size: 12px; color: var(--muted); margin: 4px 0 12px; line-height: 1.5; }

.dq-table-wrap { overflow-x: auto; }
.dq-table { width: 100%; border-collapse: collapse; min-width: 520px; }
.dq-table th { font-size: 10px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; color: var(--muted); padding: 0 12px 8px; text-align: left; border-bottom: 1px solid var(--border2); white-space: nowrap; }
.dq-table td { padding: 10px 12px; font-size: 13px; color: var(--text2); border-bottom: 1px solid var(--border2); vertical-align: middle; }
.dq-table tr:last-child td { border-bottom: none; }
.dq-table tr:hover td { background: var(--surface2); }
.dq-table .emp-name { color: var(--accent); font-weight: 600; cursor: pointer; background: none; border: none; font-family: inherit; font-size: 13px; padding: 0; text-align: left; }
.dq-table .emp-name:hover { text-decoration: underline; }
.dq-table .emp-code { color: var(--muted); font-weight: 500; font-size: 11.5px; }
.dq-cls { font-size: 10.5px; font-weight: 700; letter-spacing: .02em; text-transform: capitalize; padding: 2px 8px; border-radius: 5px; background: var(--surface2); border: 1px solid var(--border2); color: var(--text2); white-space: nowrap; }
.dq-all-clear { display: flex; align-items: center; gap: 10px; padding: 16px; border-radius: 9px; background: var(--ok-bg); border: 1px solid var(--ok-border); color: var(--ok); font-size: 13px; font-weight: 600; }
`;

function sevColor(sev) {
  return sev === "error" ? "var(--danger)" : "var(--warn)";
}
function sevBg(sev) {
  return sev === "error" ? "var(--danger-bg)" : "var(--warn-bg)";
}
function sevBorder(sev) {
  return sev === "error" ? "var(--danger-border)" : "var(--warn-border)";
}

function IconArrowLeft(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M11 18l-6-6 6-6" />
    </svg>
  );
}
function IconChevron({ open, ...props }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
         strokeLinecap="round" strokeLinejoin="round"
         style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} {...props}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export default function DataQuality() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "H").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/reports/data-quality");
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load the data quality report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const checks = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.checks;
    return data.checks
      .map(c => ({
        ...c,
        employees: c.employees.filter(e =>
          e.employee_name.toLowerCase().includes(q) || e.employee_code.toLowerCase().includes(q)),
      }))
      .map(c => ({ ...c, count: c.employees.length }));
  }, [data, search]);

  const visibleChecks = search.trim() ? checks.filter(c => c.count > 0) : checks;
  const nothingFlagged = data && data.summary.total_issues === 0;

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" />
      <style>{CSS}</style>
      <header className="adm-topbar">
        <button className="adm-back" onClick={() => navigate("/")} title="Back"><IconArrowLeft /></button>
        <div className="adm-brand">
          <div className="adm-brand-mark">🔍</div>
          <span className="adm-brand-label">HRMS & Payroll — Data Quality</span>
        </div>
        <div style={{ marginRight: 8, fontSize: 12, color: "#6080aa" }}>{user?.org_name}</div>
        <div className="adm-avatar" title={user?.name}>{initials}</div>
      </header>

      <div className="adm-tabs">
        <button className="adm-tab" onClick={() => navigate("/hrms/dashboard")}>Dashboard</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/employees")}>Employees</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/payroll")}>Payroll</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/expiry")}>Document Expiry</button>
        <button className="adm-tab active">Data Quality</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/company-documents")}>Company Documents</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/assistant")}>AI Assistant</button>
      </div>

      <div className="adm-main">
        {error && <div className="adm-error">{error}</div>}

        <div className="dq-page-head">
          <div>
            <h1>Employee Data Quality Check</h1>
            <p>Records with missing or inconsistent information that HR should review and fix.</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {data && <div className="dq-asof">As of {new Date(data.generated_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</div>}
            <button className="adm-action-btn" onClick={load} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</button>
          </div>
        </div>

        {loading ? (
          <div className="adm-spinner-wrap"><div className="adm-spinner" /></div>
        ) : data && (
          <>
            <div className="dq-tiles">
              <div className="dq-tile" style={{ "--tile-c": "var(--accent)" }}>
                <div className="n">{data.summary.total_employees}</div>
                <div className="l">Employees Checked</div>
              </div>
              <div className="dq-tile" style={{ "--tile-c": data.summary.employees_with_issues > 0 ? "var(--warn)" : "var(--ok)" }}>
                <div className="n">{data.summary.employees_with_issues}</div>
                <div className="l">Employees With Issues</div>
              </div>
              <div className="dq-tile" style={{ "--tile-c": data.summary.total_issues > 0 ? "var(--danger)" : "var(--ok)" }}>
                <div className="n">{data.summary.total_issues}</div>
                <div className="l">Total Issues Found</div>
              </div>
            </div>

            {nothingFlagged ? (
              <div className="dq-all-clear">✓ No data quality issues found — every employee record has the required information.</div>
            ) : (
              <div className="adm-card">
                <div className="adm-card-title">Issues by Category</div>
                <div className="adm-card-desc">
                  Checks come straight from the requirement spec. Click a category to see the affected employees, then click a name to open that record.
                </div>
                <div style={{ marginBottom: 14 }}>
                  <input className="adm-input" placeholder="Search by employee name or code…"
                         value={search} onChange={e => setSearch(e.target.value)} />
                </div>

                {visibleChecks.length === 0 ? (
                  <div className="adm-empty">No flagged employees match “{search}”.</div>
                ) : visibleChecks.map(c => {
                  const isOpen = expanded[c.key] ?? false;
                  const empty = c.count === 0;
                  return (
                    <div key={c.key} className="dq-check">
                      <button className="dq-check-head" disabled={empty}
                              onClick={() => setExpanded(p => ({ ...p, [c.key]: !isOpen }))}>
                        <span className="dq-sev" style={{ background: empty ? "var(--ok)" : sevColor(c.severity) }} />
                        <span className="dq-check-label">{c.label}</span>
                        <span className="dq-count" style={{
                          color: empty ? "var(--ok)" : sevColor(c.severity),
                          background: empty ? "var(--ok-bg)" : sevBg(c.severity),
                          borderColor: empty ? "var(--ok-border)" : sevBorder(c.severity),
                        }}>{c.count}</span>
                        {!empty && <span className="dq-chev"><IconChevron open={isOpen} /></span>}
                      </button>
                      {isOpen && !empty && (
                        <div className="dq-check-body">
                          <div className="desc">{c.description}</div>
                          <div className="dq-table-wrap">
                            <table className="dq-table">
                              <thead>
                                <tr><th>Employee</th><th>Type</th><th>Detail</th></tr>
                              </thead>
                              <tbody>
                                {c.employees.map(e => (
                                  <tr key={e.employee_id}>
                                    <td>
                                      <button className="emp-name"
                                              onClick={() => navigate(`/hrms/employees?edit=${e.employee_id}`)}>
                                        {e.employee_name}
                                      </button>
                                      <div className="emp-code">{e.employee_code}</div>
                                    </td>
                                    <td><span className="dq-cls">{(e.classification || "").replace(/_/g, "-")}</span></td>
                                    <td>{e.detail || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
